import { useState, useCallback } from 'react';
import styles from './SpeedTest.module.css';

const METRICS = [
  { key: 'performance', label: 'Performance' },
  { key: 'accessibility', label: 'Accessibility' },
  { key: 'best-practices', label: 'Best Practices' },
  { key: 'seo', label: 'SEO' },
];

const VITALS = [
  { key: 'lcp', scoreKey: 'lcpScore', label: 'Largest Contentful Paint' },
  { key: 'tbt', scoreKey: 'tbtScore', label: 'Total Blocking Time' },
  { key: 'cls', scoreKey: 'clsScore', label: 'Cumulative Layout Shift' },
];

function scoreColor(score) {
  if (score >= 90) return 'var(--color-success)';
  if (score >= 50) return '#F59E0B';
  return 'var(--color-error)';
}

function scoreLabel(score) {
  if (score >= 90) return 'Good';
  if (score >= 50) return 'Needs work';
  return 'Poor';
}

function ensureHttps(url) {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

function vitalColor(score) {
  if (score == null) return 'var(--color-gray-400)';
  if (score >= 0.9) return 'var(--color-success)';
  if (score >= 0.5) return '#F59E0B';
  return 'var(--color-error)';
}

function parseResult(data) {
  const cats = data.lighthouseResult?.categories ?? {};
  const audits = data.lighthouseResult?.audits ?? {};
  return {
    scores: {
      performance:     Math.round((cats.performance?.score      ?? 0) * 100),
      accessibility:   Math.round((cats.accessibility?.score    ?? 0) * 100),
      'best-practices': Math.round((cats['best-practices']?.score ?? 0) * 100),
      seo:             Math.round((cats.seo?.score              ?? 0) * 100),
    },
    vitals: {
      lcp:      audits['largest-contentful-paint']?.displayValue ?? '—',
      tbt:      audits['total-blocking-time']?.displayValue      ?? '—',
      cls:      audits['cumulative-layout-shift']?.displayValue  ?? '—',
      lcpScore: audits['largest-contentful-paint']?.score,
      tbtScore: audits['total-blocking-time']?.score,
      clsScore: audits['cumulative-layout-shift']?.score,
    },
  };
}

const ScoreRing = ({ score, label }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const fill = score != null ? circ - (circ * score) / 100 : circ;
  const color = score != null ? scoreColor(score) : 'var(--color-gray-300)';

  return (
    <div className={styles.ring}>
      <svg width="88" height="88" viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r={r} fill="none" stroke="var(--color-gray-200)" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circ}
          strokeDashoffset={fill}
          strokeLinecap="round"
          transform="rotate(-90 48 48)"
          style={{ transition: 'stroke-dashoffset 0.8s ease, stroke 0.4s' }}
        />
        <text x="48" y="48" textAnchor="middle" dominantBaseline="central"
          fontSize="18" fontWeight="700" fill={score != null ? color : 'var(--color-gray-400)'}>
          {score ?? '—'}
        </text>
      </svg>
      <span className={styles.ringLabel}>{label}</span>
      {score != null && (
        <span className={styles.ringStatus} style={{ color }}>{scoreLabel(score)}</span>
      )}
    </div>
  );
};

const SpeedTest = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const fetchStrategy = useCallback(async (target, strategy, keyParam) => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const res = await fetch(
        `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(target)}&strategy=${strategy}&category=performance&category=accessibility&category=best-practices&category=seo${keyParam}`
      );
      if (res.status === 429) throw new Error('rate-limited');
      if (res.ok) return parseResult(await res.json());
      if (res.status >= 500 && attempt === 0) {
        // Lighthouse on Google's side often flakes on slow/heavy URLs; one retry usually clears it.
        await new Promise(r => setTimeout(r, 1500));
        continue;
      }
      throw new Error(res.status >= 500 ? 'lighthouse-failed' : 'client-error');
    }
  }, []);

  const runTest = useCallback(async () => {
    if (!url.trim()) return;
    setLoading(true);
    setResults(null);
    setError(null);

    const target = ensureHttps(url.trim());
    const apiKey = import.meta.env.VITE_GOOGLE_PSI_KEY;
    const keyParam = apiKey ? `&key=${apiKey}` : '';

    const [desktopRes, mobileRes] = await Promise.allSettled([
      fetchStrategy(target, 'desktop', keyParam),
      fetchStrategy(target, 'mobile', keyParam),
    ]);
    const desktop = desktopRes.status === 'fulfilled' ? desktopRes.value : null;
    const mobile  = mobileRes.status  === 'fulfilled' ? mobileRes.value  : null;

    if (desktop || mobile) {
      setResults({ desktop, mobile });
      if (!desktop) setError('Desktop audit failed — showing mobile only. Click Run Test to retry the desktop run.');
      else if (!mobile) setError('Mobile audit failed — showing desktop only. Click Run Test to retry the mobile run.');
    } else {
      const reasons = [desktopRes, mobileRes]
        .filter(r => r.status === 'rejected')
        .map(r => r.reason?.message);
      if (reasons.includes('rate-limited')) {
        setError('Rate limit reached — add a free Google API key (VITE_GOOGLE_PSI_KEY) to continue testing.');
      } else if (reasons.includes('lighthouse-failed')) {
        setError("Google's Lighthouse engine failed on that URL — common on heavy pages. Try again, or test a lighter page on the same site.");
      } else {
        setError('Could not fetch results. Check the URL is publicly accessible and try again.');
      }
    }

    setLoading(false);
  }, [url, fetchStrategy]);

  const handleKey = e => { if (e.key === 'Enter') runTest(); };

  return (
    <div className={styles.wrap}>
      {/* Input */}
      <div className={styles.inputRow}>
        <input
          type="url"
          className={styles.urlInput}
          placeholder="https://yourwebsite.com"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={handleKey}
          disabled={loading}
        />
        <button
          className={styles.runBtn}
          onClick={runTest}
          disabled={loading || !url.trim()}
        >
          {loading ? (
            <span className={styles.spinner} aria-label="Loading" />
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
              </svg>
              Run Test
            </>
          )}
        </button>
      </div>

      {loading && (
        <div className={styles.loadingBar}>
          <div className={styles.loadingFill} />
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {/* Score rows */}
      {results && (
        <div className={styles.scoreRows}>
          {[{ label: 'Desktop', data: results.desktop }, { label: 'Mobile', data: results.mobile }]
            .filter(s => s.data)
            .map(({ label, data }) => (
            <div key={label} className={styles.strategyBlock}>
              <p className={styles.strategyLabel}>{label}</p>
              <div className={styles.rings}>
                {METRICS.map(m => (
                  <ScoreRing key={m.key} score={data.scores[m.key]} label={m.label} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Core Web Vitals comparison */}
      {results && results.desktop && results.mobile && (
        <div className={styles.vitals}>
          <p className={styles.vitalsTitle}>Core Web Vitals</p>
          <div className={styles.vitalsTable}>
            <div className={styles.vitalsHeader}>
              <span />
              <span className={styles.vitalsColLabel}>Desktop</span>
              <span className={styles.vitalsColLabel}>Mobile</span>
            </div>
            {VITALS.map(v => (
              <div key={v.key} className={styles.vitalsRow}>
                <span className={styles.vitalsMetricLabel}>{v.label}</span>
                <span className={styles.vitalsValue}>
                  <span className={styles.vitalDot} style={{ background: vitalColor(results.desktop.vitals[v.scoreKey]) }} />
                  {results.desktop.vitals[v.key]}
                </span>
                <span className={styles.vitalsValue}>
                  <span className={styles.vitalDot} style={{ background: vitalColor(results.mobile.vitals[v.scoreKey]) }} />
                  {results.mobile.vitals[v.key]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className={styles.attribution}>
        Powered by <span className={styles.attributionLink}>Google PageSpeed Insights</span>
      </p>
    </div>
  );
};

export default SpeedTest;
